// src/frontend_react/src/components/admin/SecretManagement.js
import React, { useState, useEffect, useCallback } from 'react';

// Base UI Components
import Button from '../../components/ui/Button/Button';
import Table from '../../components/ui/Table/Table';
import Select from '../../components/ui/Select/Select';
import Alert from '../../components/ui/Alert/Alert';
import Spinner from '../../components/ui/Spinner/Spinner';
import Card from '../../components/ui/Card/Card';
import Modal from '../../components/ui/Modal/Modal';
import Icon from '../../components/ui/Icon/Icon';
import Typography from '../../components/ui/Typography/Typography';
import Stack, { HStack } from '../../components/ui/Stack/Stack';
import Box from '../../components/ui/Box/Box';
import Badge from '../../components/ui/Badge/Badge';
import Input from '../../components/ui/Input/Input';

// API service
import { getAdminSecretsMetadata, generateAdminSecret, rotateAdminSecret, destroyAdminSecret, getAdminProjects } from '../../services/api';

//Icons
import { FiKey, FiPlusCircle, FiRefreshCw, FiTrash2 } from 'react-icons/fi';

const STANDARD_SECRET_NAMES = [
  { value: 'SHARED_SALT', label: 'SHARED_SALT (for Hashing Identifiers)' },
  { value: 'OUTPUT_ENCRYPTION_KEY', label: 'OUTPUT_ENCRYPTION_KEY (for Encrypting Files)' },
  // Add other standard secret names here if they become common
];


function SecretManagementPage() {
  const [secrets, setSecrets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProjectIdForList, setSelectedProjectIdForList] = useState('');
  
  const [destroyConfirmationInput, setDestroyConfirmationInput] = useState('');
  
  const [isLoadingSecrets, setIsLoadingSecrets] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [currentActionInfo, setCurrentActionInfo] = useState(null);

  const [error, setError] = useState(null);
  const [operationMessage, setOperationMessage] = useState('');

  // State for the action form within the "Manage Secrets for Project" card
  const [actionSecretName, setActionSecretName] = useState(STANDARD_SECRET_NAMES[0]?.value || '');

  const [showDestroyConfirm, setShowDestroyConfirm] = useState(false);
  const [secretToDestroy, setSecretToDestroy] = useState(null);

  const fetchProjectsForDropdown = useCallback(async () => {
    setIsLoadingProjects(true);
    setError(null);
    try {
      const projectsData = await getAdminProjects();
      const sortedProjects = Array.isArray(projectsData) ? projectsData.filter(p => p.is_active).sort((a, b) => a.name.localeCompare(b.name)) : [];
      setProjects(sortedProjects);
      // Do not auto-select a project initially to encourage explicit selection
      // if (sortedProjects.length > 0 && !selectedProjectIdForList) {
      //   setSelectedProjectIdForList(sortedProjects[0].id);
      // } else 
      if (sortedProjects.length === 0) {
        setSelectedProjectIdForList('');
        setError("No active projects available to manage secrets. Please create or activate a project first.");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch projects.");
      setProjects([]);
      setSelectedProjectIdForList('');
    } finally {
      setIsLoadingProjects(false);
    }
  }, []);

  const fetchSecrets = useCallback(async () => {
    if (!selectedProjectIdForList) {
      setSecrets([]);
      setIsLoadingSecrets(false); // Ensure loading is false if no project selected
      return;
    }
    setIsLoadingSecrets(true); 
    setError(null); 
    setOperationMessage('');
    try {
      const data = await getAdminSecretsMetadata(selectedProjectIdForList);
      setSecrets(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to fetch secrets metadata.");
      setSecrets([]);
    } finally {
      setIsLoadingSecrets(false);
    }
  }, [selectedProjectIdForList]);

  useEffect(() => {
    fetchProjectsForDropdown();
  }, [fetchProjectsForDropdown]);

  useEffect(() => {
    // This effect runs when selectedProjectIdForList changes
    if (selectedProjectIdForList) {
      fetchSecrets();
    } else {
      setSecrets([]); // Clear secrets if no project is selected
    }
  }, [selectedProjectIdForList, fetchSecrets]);


  const handleAction = async (actionType) => {
    if (!selectedProjectIdForList || !actionSecretName) {
      setError("Project and Secret Name are required for this action.");
      return;
    }
    const projectDisplayName = projects.find(p => p.id === selectedProjectIdForList)?.name || selectedProjectIdForList;
    const confirmMessage = actionType === 'generate'
      ? `Are you sure you want to generate/update the secret '${actionSecretName}' for project '${projectDisplayName}'? This will replace any existing active secret with this name.`
      : `Are you sure you want to rotate the secret '${actionSecretName}' for project '${projectDisplayName}'? This will invalidate the old active secret and create a new one.`;

    if (!window.confirm(confirmMessage)) return;

    setCurrentActionInfo({ name: actionSecretName, type: actionType });
    setIsSubmittingAction(true);
    setOperationMessage(`Processing '${actionType}' for secret '${actionSecretName}'...`);
    setError(null);

    try {
      let result;
      if (actionType === 'generate') {
        result = await generateAdminSecret(selectedProjectIdForList, actionSecretName);
      } else if (actionType === 'rotate') {
        result = await rotateAdminSecret(selectedProjectIdForList, actionSecretName);
      }
      setOperationMessage(`Secret '${result?.name || actionSecretName}' for project '${projectDisplayName}' ${actionType}d successfully.`);
      fetchSecrets(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to ${actionType} secret.`);
      setOperationMessage('');
    } finally {
      setIsSubmittingAction(false);
      setCurrentActionInfo(null);
    }
  };
  const openDestroyConfirm = useCallback((secret) => {
    setSecretToDestroy(secret);
    setDestroyConfirmationInput('');
    setShowDestroyConfirm(true);
  }, []); 

  const closeDestroyConfirm = () => {
    setSecretToDestroy(null);
    setShowDestroyConfirm(false);
  };

  const handleDestroy = async () => {
    if (!secretToDestroy) return;
    setIsSubmittingAction(true);
    setOperationMessage(''); // Clear previous messages
    setError(null);
    try {
      await destroyAdminSecret(secretToDestroy.id, secretToDestroy.project_id); // Pass project_id for context
      setOperationMessage(`Secret ID '${secretToDestroy.id}' (${secretToDestroy.name}) for project '${secretToDestroy.project_id}' marked as destroyed successfully.`);
      fetchSecrets(); // Refresh the list
    } catch (err) {
      setError(err.response?.data?.message || err.message || `Failed to destroy secret '${secretToDestroy.name}'.`);
      // setOperationMessage(''); // Error message is more important
    } finally {
      setIsSubmittingAction(false);
      closeDestroyConfirm();
    }
  };

  const columns = React.useMemo(() => [
    { Header: 'ID', accessor: 'id', width: 50 },
    { Header: 'Name', accessor: 'name', Cell: ({value}) => <Typography variant="body2" component="code">{value}</Typography> },
    { 
      Header: 'Status', accessor: 'is_active', width: 100,
      Cell: ({ value }) => <Badge variant={value ? 'success' : 'default'} size="sm">{value ? 'Active' : 'Inactive'}</Badge>
    },
    { Header: 'Created At', accessor: 'created_at', width: 180, Cell: ({ value }) => value ? new Date(value).toLocaleString() : '-' },
    { Header: 'Rotated At', accessor: 'rotated_at', width: 180, Cell: ({ value }) => value ? new Date(value).toLocaleString() : '-' },
    { Header: 'Destroyed At', accessor: 'destroyed_at', width: 180, Cell: ({ value }) => value ? new Date(value).toLocaleString() : '-' },
    {
      Header: 'Actions',
      id: 'actions',
      width: 150,
      Cell: ({ row }) => {
        const secret = row; // row is the actual secret object in your table
        return (
          <Box>
            {secret.is_active && !secret.destroyed_at && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => openDestroyConfirm(secret)}
                leftIcon={<Icon as={FiTrash2} />}
                disabled={isSubmittingAction || isLoadingProjects || isLoadingSecrets}
                aria-label={`Destroy secret ${secret.name} (ID: ${secret.id})`}
              >
                Destroy
              </Button>
            )}
            {(!secret.is_active || secret.destroyed_at) && (
              <Typography variant="caption" color="placeholder">N/A</Typography>
            )}
          </Box>
        );
      }
    }
  ], [isSubmittingAction, isLoadingProjects, isLoadingSecrets, openDestroyConfirm]); // Added openDestroyConfirm

  const projectOptions = projects.map(proj => ({ value: proj.id, label: `${proj.name} (ID: ${proj.id})` }));
  const secretNameOptions = STANDARD_SECRET_NAMES;
  const currentProjectDisplayName = projects.find(p => p.id === selectedProjectIdForList)?.name || selectedProjectIdForList;


  if (isLoadingProjects && projects.length === 0) {
    return (
      <Card title="Secret Management" titleIcon={<Icon as={FiKey} />}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py="xl">
          <Spinner size="lg" /><Typography mt="md" color="secondary">Loading projects...</Typography>
        </Box>
      </Card>
    );
  }
  
  return (
    <Card title="Secret Management" titleIcon={<Icon as={FiKey} />}>
      <Stack spacing="xl">
        <Typography variant="body1" color="secondary">
          View and manage cryptographic secrets (e.g., SALTs, Encryption Keys) for projects.
          Handle with extreme care. These secrets are vital for data security.
        </Typography>

        {error && <Alert variant="error" title="Error" onDismiss={() => setError(null)}>{error}</Alert>}
        {operationMessage && <Alert variant="info" onDismiss={() => setOperationMessage('')}>{operationMessage}</Alert>}

        <Box style={{ maxWidth: '450px' }}> {/* Constrain width of the project filter Select */}
            <Select
            label="Filter by Project:" id="projectFilterSecrets"
            value={selectedProjectIdForList}
            onChange={(e) => setSelectedProjectIdForList(e.target.value)}
            options={projectOptions}
            placeholder={projects.length > 0 ? "-- Select a Project to View Secrets --" : "No active projects available"}
            disabled={projects.length === 0 || isLoadingProjects || isLoadingSecrets || isSubmittingAction}
            />
        </Box>

        {selectedProjectIdForList && (
          <Card 
            title={`Manage Secrets for Project: ${currentProjectDisplayName}`} 
            elevation="sm" // Slightly less elevation for nested card
            style={{ backgroundColor: 'var(--cipher-color-background-page)' }} // Subtle background difference
          >
            <Stack spacing="lg" p="md"> {/* Add padding inside the action card */}
              <Select
                label="Select Secret Type to Manage:" id="actionSecretName"
                value={actionSecretName}
                onChange={(e) => setActionSecretName(e.target.value)}
                options={secretNameOptions}
                disabled={isSubmittingAction || isLoadingSecrets}
              />
              <HStack spacing="md">
                <Button
                  onClick={() => handleAction('generate')} variant="primary"
                  leftIcon={<Icon as={FiPlusCircle} />}
                  disabled={!actionSecretName || isSubmittingAction || isLoadingSecrets}
                  isLoading={isSubmittingAction && currentActionInfo?.name === actionSecretName && currentActionInfo?.type === 'generate'}
                >
                  Generate / Update
                </Button>
                <Button
                  onClick={() => handleAction('rotate')} variant="warning"
                  leftIcon={<Icon as={FiRefreshCw} />}
                  disabled={!actionSecretName || isSubmittingAction || isLoadingSecrets}
                  isLoading={isSubmittingAction && currentActionInfo?.name === actionSecretName && currentActionInfo?.type === 'rotate'}
                >
                  Rotate Secret
                </Button>
              </HStack>
              <Typography variant="caption" color="secondary">
                <strong>Generate/Update:</strong> Creates the secret if it doesn't exist or replaces the current active one.
                <br/>
                <strong>Rotate:</strong> Deactivates the current active secret and creates a new active one. Both remain in history.
              </Typography>
            </Stack>
          </Card>
        )}

        {isLoadingSecrets && selectedProjectIdForList && (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py="lg">
            <Spinner size="lg" /><Typography mt="md" color="secondary">Loading secrets for {currentProjectDisplayName}...</Typography>
          </Box>
        )}

        {!isLoadingSecrets && selectedProjectIdForList && secrets.length === 0 && (
          <Alert variant="info" mt="lg">No secrets metadata found for project {currentProjectDisplayName}. Use the controls above to generate them.</Alert>
        )}

        {!isLoadingSecrets && secrets.length > 0 && (
          <Box mt="lg"> {/* Margin top for the table */}
            <Typography variant="h5" gutterBottom>Secrets History for: {currentProjectDisplayName}</Typography>
            <Table columns={columns} data={secrets} isLoading={isLoadingSecrets} />
          </Box>
        )}
        
        {!selectedProjectIdForList && projects.length > 0 && !isLoadingProjects && (
          <Alert variant="info" mt="lg">Please select a project to view and manage its secrets.</Alert>
        )}
        
        {showDestroyConfirm && secretToDestroy && (
          <Modal
            title={`Confirm Destroy Secret: ${secretToDestroy.name}`}
            isOpen={showDestroyConfirm}
            onClose={closeDestroyConfirm}
            size="md"
            footer={
              <HStack spacing="sm" justifyContent="flex-end">
                <Button variant="outline" onClick={closeDestroyConfirm} disabled={isSubmittingAction}>Cancel</Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDestroy} 
                  isLoading={isSubmittingAction} 
                  disabled={isSubmittingAction || destroyConfirmationInput !== secretToDestroy.name}
                >
                  Confirm Destroy
                </Button>
              </HStack>
            }
          >
            <Stack spacing="md">
              <Typography>
                Are you sure you want to mark secret "<strong>{secretToDestroy.name}</strong>" (ID: {secretToDestroy.id}) as destroyed (inactive)?
              </Typography>
              <Alert variant="error" title="Critical Warning">
                This action is irreversible and may lead to permanent data loss if this is the only active key.
              </Alert>
              <Input
                label={<span>Please type <strong>{secretToDestroy.name}</strong> to confirm.</span>}
                id="destroy-secret-confirm-input"
                value={destroyConfirmationInput}
                onChange={(e) => setDestroyConfirmationInput(e.target.value)}
                autoFocus
                disabled={isSubmittingAction}
              />
            </Stack>
          </Modal>
        )}
      </Stack>
    </Card>
  );
}

export default SecretManagementPage;